import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { LOGIN } from 'configs/routeNames';
import { handleSessionCleanup } from 'utils/axios';
import { useTranslation } from 'react-i18next';
import { saveToLocal, getFromLocal, removeFromLocal } from 'utils/cache';
import debounce from 'lodash/debounce';
import { LAST_ACTIVITY_KEY, WARNING_SHOWN_KEY } from 'configs/constants';
import { VITE_AUTO_LOGOUT_MINUTES } from 'configs';

const INACTIVITY_TIMEOUT = VITE_AUTO_LOGOUT_MINUTES * 60 * 1000;
const WARNING_BEFORE_TIMEOUT = 60 * 1000;

export const useAutoLogout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showWarning, setShowWarning] = useState(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const remainingTimeRef = useRef<number>(WARNING_BEFORE_TIMEOUT);
  const modalInstance = useRef<ReturnType<typeof Modal.confirm> | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());

  const logout = async () => {
    setShowWarning(false);
    // Close the modal if it's open
    if (modalInstance.current) {
      modalInstance.current.destroy();
      modalInstance.current = null;
    }
    // Clear countdown timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    await handleSessionCleanup();
    navigate(`/${LOGIN}`, { replace: true, state: {} });
  };

  const resetTimer = useCallback(() => {
    if (!modalInstance.current) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }

      setShowWarning(false);

      // Update last activity timestamp in localStorage using cache utility
      saveToLocal(LAST_ACTIVITY_KEY, Date.now().toString(), false);
      removeFromLocal(WARNING_SHOWN_KEY);

      // Set warning timer
      warningTimerRef.current = setTimeout(() => {
        // Check if another tab has already shown the warning
        if (!getFromLocal(WARNING_SHOWN_KEY, false)) {
          saveToLocal(WARNING_SHOWN_KEY, 'true', false);
          setShowWarning(true);
          // Reset remaining time
          remainingTimeRef.current = WARNING_BEFORE_TIMEOUT;
        }
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);

      // Set logout timer
      inactivityTimerRef.current = setTimeout(logout, INACTIVITY_TIMEOUT);
    }
  }, [navigate]);

  // Debounced version of handleActivity to prevent excessive timer resets
  const debouncedHandleActivity = useCallback(
    debounce(() => {
      // Only reset timer if at least 5 seconds have passed since last activity
      const now = Date.now();
      if (now - lastActivityTimeRef.current >= 5000) {
        lastActivityTimeRef.current = now;
        resetTimer();
      }
    }, 1000),
    [resetTimer],
  );

  const handleActivity = useCallback(() => {
    debouncedHandleActivity();
  }, [debouncedHandleActivity]);

  // Listen for storage events to sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        // Another tab updated the last activity time
        // Close the modal if it's open
        if (modalInstance.current) {
          modalInstance.current.destroy();
          modalInstance.current = null;
        }
        setShowWarning(false);
        resetTimer();
      } else if (e.key === WARNING_SHOWN_KEY && e.newValue === 'true') {
        // Another tab showed the warning
        setShowWarning(true);
        // Reset remaining time
        remainingTimeRef.current = WARNING_BEFORE_TIMEOUT;
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [resetTimer]);

  // Format time in seconds to mm:ss format
  const formatTime = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (showWarning) {
      // Start countdown timer
      countdownTimerRef.current = setInterval(() => {
        remainingTimeRef.current -= 1000;

        // Update modal content with new time
        if (modalInstance.current) {
          const content = t('sessionTimeOut.content').replace('1 minute', formatTime(remainingTimeRef.current));
          modalInstance.current.update({
            content,
          });
        }

        // Clear interval when time is up
        if (remainingTimeRef.current <= 0) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
        }
      }, 1000);

      // Store the modal instance so we can close it later
      modalInstance.current = Modal.confirm({
        title: t('sessionTimeOut.title'),
        content: t('sessionTimeOut.content').replace('1 minute', formatTime(remainingTimeRef.current)),
        okText: t('sessionTimeOut.stayLoggedIn'),
        onOk: () => {
          if (modalInstance.current) {
            setShowWarning(false);
            modalInstance.current.destroy();
            modalInstance.current = null;
          }
          resetTimer();
        },
        cancelText: t('sessionTimeOut.logout'),
        onCancel: () => {
          logout();
        },
        closable: false,
        centered: true,
      });
    }

    return () => {
      // Clean up countdown timer when component unmounts or warning is hidden
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [showWarning, resetTimer, logout, t]);

  useEffect(() => {
    // Events to monitor for user activity - only meaningful interactions
    const events = ['click', 'keydown', 'mousedown', 'touchstart'];

    // Initialize timer
    resetTimer();

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Cleanup function
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      // Close the modal if it's open
      if (modalInstance.current) {
        modalInstance.current.destroy();
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      // Cancel any pending debounced calls
      debouncedHandleActivity.cancel();
    };
  }, [handleActivity, resetTimer, debouncedHandleActivity]);
};
